import {
  toDomainValue,
  type DataItem,
  type Mapping,
} from '@pn/core/domain/data';
import type {
  IDataItemMapper,
  IDataItemValueMapper,
} from '@pn/core/mappers/data';
import { assert } from '@pn/core/utils/assert';
import { log } from '@pn/core/utils/debug';
import { nullToUndefined, undefinedToNull } from '@pn/core/utils/logic';
import { apiDataItemValueMapper } from '@pn/services/api/data/apiDataItemMapper';
import { hashObject } from '@pn/services/utils/hash';
import { isNil, isNumber, isString, isUndefined } from 'lodash-es';
import type { GeoJsonDataItem, GeoJsonValue } from './types';

const warningsIssued = new Set<number>();

export const geoJsonDataItemMapper = (
  mapping: Mapping
): IDataItemMapper<GeoJsonDataItem> => {
  return {
    toDomainDataItem: (geoJsonDataItem) => {
      assert(
        isUndefined(geoJsonDataItem.internal_id) || // not needed for exports
          isString(geoJsonDataItem.internal_id) ||
          isNumber(geoJsonDataItem.internal_id),
        'GeoJSON mapping failed: internal_id must be a (string | number)'
      );

      /**
       * Numeric IDs work fine in layers with predefined mappings. However, for
       * custom GeoJSONs, URL query IDs are always parsed as strings, so we
       * have to convert `internal_id` to a string.
       */
      if (isNumber(geoJsonDataItem.internal_id)) {
        const hash = hashObject(mapping);
        if (!warningsIssued.has(hash)) {
          log().warn(`Numeric internal_id's are not supported in GeoJSONs`);
          warningsIssued.add(hash);
        }
      }

      return Object.entries(mapping).reduce<DataItem>(
        (dataItem, [field, mappingItem]) => {
          if (field === 'geometry' || field === 'internal_id') return dataItem;

          const fromValue = geoJsonDataItem[field];
          const toValue = geoJsonDataItemValueMapper.toDomainValue(
            fromValue,
            mappingItem!
          );

          dataItem[field] = toValue;
          return dataItem;
        },
        {
          _id: geoJsonDataItem.internal_id.toString(),
          internal_id: geoJsonDataItem.internal_id.toString(),
          geometry: nullToUndefined(geoJsonDataItem.geometry),
        }
      );
    },
    toTargetDataItem: (dataItem) => {
      return Object.entries(mapping).reduce<GeoJsonDataItem>(
        (targetItem, [field, mappingItem]) => {
          if (field === 'internal_id' || field === 'geometry')
            return targetItem;

          const value = dataItem[field];
          const toValue = geoJsonDataItemValueMapper.toTargetValue(
            toDomainValue(value, mappingItem!)
          );

          targetItem[field] = toValue;
          return targetItem;
        },
        {
          internal_id: dataItem._id,
          geometry: undefinedToNull(dataItem.geometry),
        }
      );
    },
  };
};

const geoJsonDataItemValueMapper: IDataItemValueMapper<GeoJsonValue> = {
  toDomainValue: apiDataItemValueMapper.toDomainValue,
  toTargetValue: ({ value, domainType }) => {
    if (isNil(value)) return undefined;

    switch (domainType) {
      case 'string':
        return value;
      case 'number':
        return value;
      case 'boolean':
        return value;
      case 'object':
        return JSON.stringify(value);
      case 'DateString':
        return value;
      case 'SIUnit':
        return value.value; // no unit conversion for exports
      case 'Geometry':
        return value;
      default:
        return domainType satisfies never;
    }
  },
};
